/**
 * Planner Page — Calendar-first planning hub.
 *
 * Two-tab layout:
 *   1. Calendar (default) — Full month grid showing a unified feed of tasks,
 *      events, and all application entity dates (WOs, POs, SOs, deals, auctions,
 *      recurring). Day-click opens a detail sidebar with item list and inline
 *      creation for events and tasks.
 *   2. Notes — Unchanged note grid with create / edit / delete.
 *
 * Data flows through the `/api/v1/planner/calendar-feed` endpoint which returns
 * `CalendarItem[]`. Mutations go to the individual task/event endpoints. The
 * calendar feed query is keyed on `[start, end]` of the visible grid so it
 * automatically refetches when the user navigates months.
 */

import { useState, useMemo, useCallback } from "react";
import {
  Calendar,
  StickyNote,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  X,
  ExternalLink,
  Clock,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CalendarItem {
  id: string;
  title: string;
  date: string;
  end_date: string | null;
  all_day: boolean;
  source: string;
  source_label: string;
  entity_type: string | null;
  entity_id: string | null;
  color: string; // "brand" | "warning" | "info" | "accent" | "success" | "danger"
  status: string | null;
  priority: string | null;
  description: string | null;
  link: string | null;
}

type PlannerTab = "calendar" | "notes";
type CreateType = "event" | "task";
type CalendarView = "day" | "week" | "month";

// ---------------------------------------------------------------------------
// Helpers — date arithmetic (no external lib)
// ---------------------------------------------------------------------------

/** Return the Sunday on or before `date`. */
function startOfWeek(d: Date): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() - copy.getDay());
  return copy;
}

/** Return the Saturday on or after `date`. */
function endOfWeek(d: Date): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + (6 - copy.getDay()));
  return copy;
}

/** First day of month. */
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/** Last day of month. */
function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

/** Inclusive array of dates between two dates. */
function dateRange(start: Date, end: Date): Date[] {
  const result: Date[] = [];
  const cur = new Date(start);
  while (cur <= end) {
    result.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return result;
}

/** YYYY-MM-DD string for a Date (local timezone). */
function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Check two dates share the same calendar day. */
function isSameDay(a: Date, b: Date): boolean {
  return toDateKey(a) === toDateKey(b);
}

/** Format a date as "Thursday, April 17, 2026". */
function formatLongDate(d: Date): string {
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** Format month/year header. */
function formatMonthYear(d: Date): string {
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

// ---------------------------------------------------------------------------
// Color mapping — CalendarItem.color -> CSS-variable-based Tailwind classes
// ---------------------------------------------------------------------------

const COLOR_MAP: Record<string, { bg: string; text: string; dot: string }> = {
  brand: {
    bg: "bg-[var(--color-interactive-primary)]/15",
    text: "text-[var(--color-interactive-primary)]",
    dot: "bg-[var(--color-interactive-primary)]",
  },
  warning: {
    bg: "bg-[var(--fill-warning)]",
    text: "text-[var(--text-warning)]",
    dot: "bg-[var(--text-warning)]",
  },
  info: {
    bg: "bg-[var(--fill-info)]",
    text: "text-[var(--text-info)]",
    dot: "bg-[var(--text-info)]",
  },
  accent: {
    bg: "bg-[var(--fill-info)]",
    text: "text-[var(--text-info)]",
    dot: "bg-[var(--color-interactive-primary)]",
  },
  success: {
    bg: "bg-[var(--fill-success)]",
    text: "text-[var(--text-success)]",
    dot: "bg-[var(--text-success)]",
  },
  danger: {
    bg: "bg-[var(--fill-danger)]",
    text: "text-[var(--text-danger)]",
    dot: "bg-[var(--text-danger)]",
  },
};

function colorClasses(color: string): {
  bg: string;
  text: string;
  dot: string;
} {
  return COLOR_MAP[color] ?? COLOR_MAP.brand!;
}

// ---------------------------------------------------------------------------
// Source filter definitions — label, color key, and the `source` values they
// match in the calendar feed response.
// ---------------------------------------------------------------------------

const SOURCE_FILTERS: { key: string; label: string; color: string }[] = [
  { key: "event", label: "Events", color: "brand" },
  { key: "task", label: "Tasks", color: "warning" },
  { key: "work_order", label: "WO", color: "info" },
  { key: "purchase_order", label: "PO", color: "accent" },
  { key: "sales_order", label: "SO", color: "success" },
  { key: "deal", label: "Deals", color: "warning" },
  { key: "auction", label: "Auctions", color: "danger" },
  { key: "recurring", label: "Recurring", color: "info" },
];

// ---------------------------------------------------------------------------
// Calendar Tab
// ---------------------------------------------------------------------------

function CalendarTab() {
  const qc = useQueryClient();
  const today = useMemo(() => new Date(), []);

  // ---- State ----
  const [viewMode, setViewMode] = useState<CalendarView>("month");
  const [currentMonth, setCurrentMonth] = useState<Date>(
    () => new Date(today.getFullYear(), today.getMonth(), 1),
  );
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date(today));
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [enabledSources, setEnabledSources] = useState<Set<string>>(
    () => new Set(SOURCE_FILTERS.map((s) => s.key)),
  );
  const [showCreate, setShowCreate] = useState(false);
  const [createType, setCreateType] = useState<CreateType>("event");

  // ---- Grid computation (adapts to view mode) ----
  const gridStart = useMemo(() => {
    if (viewMode === "day")
      return new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate(),
      );
    if (viewMode === "week") return startOfWeek(currentDate);
    return startOfWeek(startOfMonth(currentMonth));
  }, [viewMode, currentMonth, currentDate]);

  const gridEnd = useMemo(() => {
    if (viewMode === "day")
      return new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate(),
      );
    if (viewMode === "week") return endOfWeek(currentDate);
    return endOfWeek(endOfMonth(currentMonth));
  }, [viewMode, currentMonth, currentDate]);

  const gridDays = useMemo(
    () => dateRange(gridStart, gridEnd),
    [gridStart, gridEnd],
  );

  // ---- Calendar feed query ----
  const feedStartISO = gridStart.toISOString();
  const feedEndISO = new Date(gridEnd.getTime() + 86400000 - 1).toISOString(); // end of last day

  const { data: feedItems = [], isLoading } = useQuery<CalendarItem[]>({
    queryKey: ["planner-calendar-feed", feedStartISO, feedEndISO],
    queryFn: async () =>
      (
        await apiClient.get("/api/v1/planner/calendar-feed", {
          params: { start: feedStartISO, end: feedEndISO },
        })
      ).data,
  });

  // ---- Group items by date key, respecting source filter ----
  const filteredItems = useMemo(
    () => feedItems.filter((item) => enabledSources.has(item.source)),
    [feedItems, enabledSources],
  );

  const itemsByDate = useMemo(() => {
    const map: Record<string, CalendarItem[]> = {};
    for (const item of filteredItems) {
      const key = item.date.slice(0, 10); // "YYYY-MM-DD"
      (map[key] ??= []).push(item);
    }
    return map;
  }, [filteredItems]);

  // ---- Source counts for filter chips ----
  const sourceCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const item of feedItems) {
      counts[item.source] = (counts[item.source] ?? 0) + 1;
    }
    return counts;
  }, [feedItems]);

  // ---- Items for the currently selected day ----
  const selectedDayItems = useMemo(() => {
    if (!selectedDate) return [];
    return itemsByDate[toDateKey(selectedDate)] ?? [];
  }, [selectedDate, itemsByDate]);

  // ---- Navigation (adapts to view mode) ----
  const goToPrev = useCallback(() => {
    if (viewMode === "day")
      setCurrentDate((d) => {
        const n = new Date(d);
        n.setDate(n.getDate() - 1);
        return n;
      });
    else if (viewMode === "week")
      setCurrentDate((d) => {
        const n = new Date(d);
        n.setDate(n.getDate() - 7);
        return n;
      });
    else setCurrentMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  }, [viewMode]);

  const goToNext = useCallback(() => {
    if (viewMode === "day")
      setCurrentDate((d) => {
        const n = new Date(d);
        n.setDate(n.getDate() + 1);
        return n;
      });
    else if (viewMode === "week")
      setCurrentDate((d) => {
        const n = new Date(d);
        n.setDate(n.getDate() + 7);
        return n;
      });
    else setCurrentMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  }, [viewMode]);

  const goToToday = useCallback(() => {
    const t = new Date(today);
    setCurrentDate(t);
    setCurrentMonth(new Date(t.getFullYear(), t.getMonth(), 1));
    if (viewMode === "day") setSelectedDate(t);
  }, [today, viewMode]);

  /** Title shown in the navigation header. */
  const navTitle = useMemo(() => {
    if (viewMode === "day") return formatLongDate(currentDate);
    if (viewMode === "week") {
      const ws = startOfWeek(currentDate);
      const we = endOfWeek(currentDate);
      const fmt = (d: Date) =>
        d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      return `${fmt(ws)} – ${fmt(we)}, ${we.getFullYear()}`;
    }
    return formatMonthYear(currentMonth);
  }, [viewMode, currentDate, currentMonth]);

  // ---- Source toggle ----
  const toggleSource = useCallback((key: string) => {
    setEnabledSources((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // ---- Mutations (tasks & events) ----
  const createTaskMutation = useMutation({
    mutationFn: async (data: {
      title: string;
      priority: string;
      due_date: string;
    }) => (await apiClient.post("/api/v1/planner/tasks", data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["planner-calendar-feed"] });
      qc.invalidateQueries({ queryKey: ["planner-tasks"] });
      toast.success("Task created");
      setShowCreate(false);
    },
    onError: () => toast.error("Failed to create task"),
  });

  const createEventMutation = useMutation({
    mutationFn: async (data: {
      title: string;
      start_at: string;
      end_at?: string;
      all_day: boolean;
    }) => (await apiClient.post("/api/v1/planner/events", data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["planner-calendar-feed"] });
      qc.invalidateQueries({ queryKey: ["planner-events"] });
      toast.success("Event created");
      setShowCreate(false);
    },
    onError: () => toast.error("Failed to create event"),
  });

  // ---- Inline create form state ----
  const [createTitle, setCreateTitle] = useState("");
  const [createAllDay, setCreateAllDay] = useState(true);
  const [createStartTime, setCreateStartTime] = useState("09:00");
  const [createEndTime, setCreateEndTime] = useState("10:00");
  const [createPriority, setCreatePriority] = useState("medium");

  const handleCreateSubmit = () => {
    if (!createTitle.trim() || !selectedDate) return;
    const dateStr = toDateKey(selectedDate);
    if (createType === "event") {
      const start_at = createAllDay
        ? new Date(`${dateStr}T00:00:00`).toISOString()
        : new Date(`${dateStr}T${createStartTime}:00`).toISOString();
      const end_at = createAllDay
        ? undefined
        : new Date(`${dateStr}T${createEndTime}:00`).toISOString();
      createEventMutation.mutate({
        title: createTitle,
        start_at,
        end_at,
        all_day: createAllDay,
      });
    } else {
      createTaskMutation.mutate({
        title: createTitle,
        priority: createPriority,
        due_date: new Date(`${dateStr}T23:59:59`).toISOString(),
      });
    }
    setCreateTitle("");
  };

  // ---- Day names header ----
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="flex gap-0">
      {/* ================================================================
          Main calendar area
          ================================================================ */}
      <div
        className={`flex-1 min-w-0 transition-all duration-200 ${selectedDate ? "pr-2" : ""}`}
      >
        {/* Navigation header */}
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrev}
              className="rounded-[var(--radius-md)] p-1.5 text-[var(--text-secondary)] hover:bg-[var(--surface-base)] transition-colors"
              aria-label="Previous"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <h3 className="text-base font-semibold text-[var(--text-primary)] min-w-[180px] text-center">
              {navTitle}
            </h3>
            <button
              onClick={goToNext}
              className="rounded-[var(--radius-md)] p-1.5 text-[var(--text-secondary)] hover:bg-[var(--surface-base)] transition-colors"
              aria-label="Next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <Button variant="secondary" size="sm" onClick={goToToday}>
              Today
            </Button>
          </div>
          <div className="flex items-center gap-1">
            {/* View mode toggle */}
            <div className="inline-flex rounded-[var(--radius-md)] border border-[var(--border-default)] p-0.5 bg-[var(--surface-sunken)]">
              {(["day", "week", "month"] as CalendarView[]).map((v) => (
                <button
                  key={v}
                  onClick={() => setViewMode(v)}
                  className={`rounded-[var(--radius-sm)] px-2.5 py-1 text-[11px] font-medium capitalize transition-all ${
                    viewMode === v
                      ? "bg-[var(--surface-base)] text-[var(--text-primary)] shadow-sm border border-[var(--border-default)]"
                      : "text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
            {isLoading && <LoadingSpinner size="sm" />}
          </div>
        </div>

        {/* Source filter chips */}
        <div className="mb-3 flex flex-wrap gap-1.5">
          {SOURCE_FILTERS.map((sf) => {
            const active = enabledSources.has(sf.key);
            const c = colorClasses(sf.color);
            const count = sourceCounts[sf.key] ?? 0;
            return (
              <button
                key={sf.key}
                onClick={() => toggleSource(sf.key)}
                className={`inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] px-2.5 py-1 text-[11px] font-medium border transition-colors ${
                  active
                    ? `${c.bg} ${c.text} border-current/20`
                    : "bg-[var(--surface-sunken)] text-[var(--text-tertiary)] border-[var(--border-default)] opacity-60"
                }`}
              >
                <span
                  className={`inline-block h-2 w-2 rounded-full ${active ? c.dot : "bg-[var(--text-tertiary)]"}`}
                />
                {sf.label}
                {count > 0 && (
                  <span className="ml-0.5 opacity-70">{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* ============ DAY VIEW ============ */}
        {viewMode === "day" &&
          (() => {
            const dayKey = toDateKey(currentDate);
            const dayItems = itemsByDate[dayKey] || [];
            const hours = Array.from({ length: 24 }, (_, i) => i);
            return (
              <div className="border border-[var(--border-default)] rounded-[var(--radius-lg)] overflow-hidden bg-[var(--surface-base)]">
                {/* All-day section */}
                {dayItems.filter((it) => it.all_day).length > 0 && (
                  <div className="border-b border-[var(--border-default)] px-3 py-2 bg-[var(--surface-base)]">
                    <p className="text-[9px] font-medium uppercase tracking-wider text-[var(--text-tertiary)] mb-1">
                      All day
                    </p>
                    <div className="page-wide space-y-1">
                      {dayItems
                        .filter((it) => it.all_day)
                        .map((it) => {
                          const c = colorClasses(it.color);
                          return (
                            <div
                              key={it.id}
                              className={`rounded-[var(--radius-sm)] px-2 py-1 text-xs font-medium ${c.bg} ${c.text}`}
                            >
                              {it.source_label} · {it.title}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
                {/* Hourly timeline */}
                <div className="max-h-[calc(100vh-380px)] overflow-y-auto">
                  {hours.map((h) => {
                    const hourItems = dayItems.filter((it) => {
                      if (it.all_day) return false;
                      const d = new Date(it.date);
                      return d.getHours() === h;
                    });
                    const label =
                      h === 0
                        ? "12 AM"
                        : h < 12
                          ? `${h} AM`
                          : h === 12
                            ? "12 PM"
                            : `${h - 12} PM`;
                    return (
                      <div
                        key={h}
                        className="flex border-b border-[var(--border-subtle)] min-h-[48px]"
                        onClick={() => {
                          setSelectedDate(currentDate);
                        }}
                      >
                        <div className="w-16 shrink-0 py-1.5 pr-2 text-right text-[10px] text-[var(--text-tertiary)] border-r border-[var(--border-subtle)]">
                          {label}
                        </div>
                        <div className="flex-1 px-2 py-1 space-y-0.5">
                          {hourItems.map((it) => {
                            const c = colorClasses(it.color);
                            return (
                              <div
                                key={it.id}
                                className={`rounded-[var(--radius-sm)] px-2 py-1 text-xs font-medium ${c.bg} ${c.text}`}
                              >
                                {it.title}
                                {it.status && (
                                  <span className="ml-1 opacity-60">
                                    ({it.status})
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

        {/* ============ WEEK VIEW ============ */}
        {viewMode === "week" &&
          (() => {
            const weekDays = gridDays.slice(0, 7);
            const hours = Array.from({ length: 16 }, (_, i) => i + 6); // 6 AM to 9 PM
            return (
              <div className="border border-[var(--border-default)] rounded-[var(--radius-lg)] overflow-hidden bg-[var(--surface-base)]">
                {/* Day headers */}
                <div
                  className="grid border-b border-[var(--border-default)]"
                  style={{ gridTemplateColumns: "64px repeat(7, 1fr)" }}
                >
                  <div className="border-r border-[var(--border-subtle)]" />
                  {weekDays.map((day) => {
                    const isToday = isSameDay(day, today);
                    return (
                      <div
                        key={toDateKey(day)}
                        className="py-2 text-center border-r border-[var(--border-subtle)] last:border-r-0"
                        onClick={() => {
                          setSelectedDate(day);
                        }}
                      >
                        <p className="text-[10px] font-medium uppercase text-[var(--text-tertiary)]">
                          {day.toLocaleDateString("en-US", {
                            weekday: "short",
                          })}
                        </p>
                        <p
                          className={`text-lg font-semibold mt-0.5 ${
                            isToday
                              ? "text-[var(--color-interactive-primary)]"
                              : "text-[var(--text-primary)]"
                          }`}
                        >
                          {day.getDate()}
                        </p>
                      </div>
                    );
                  })}
                </div>

                {/* All-day row */}
                <div
                  className="grid border-b border-[var(--border-default)] bg-[var(--surface-base)]"
                  style={{ gridTemplateColumns: "64px repeat(7, 1fr)" }}
                >
                  <div className="border-r border-[var(--border-subtle)] py-1 pr-2 text-right text-[9px] text-[var(--text-tertiary)]">
                    All day
                  </div>
                  {weekDays.map((day) => {
                    const dk = toDateKey(day);
                    const allDayItems = (itemsByDate[dk] || []).filter(
                      (it) => it.all_day,
                    );
                    return (
                      <div
                        key={dk}
                        className="border-r border-[var(--border-subtle)] last:border-r-0 px-1 py-1 space-y-0.5"
                      >
                        {allDayItems.slice(0, 2).map((it) => {
                          const c = colorClasses(it.color);
                          return (
                            <div
                              key={it.id}
                              className={`rounded-[var(--radius-sm)] px-1 py-0.5 text-[9px] font-medium truncate ${c.bg} ${c.text}`}
                            >
                              {it.title}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                {/* Hourly grid */}
                <div className="max-h-[calc(100vh-420px)] overflow-y-auto">
                  {hours.map((h) => {
                    const label =
                      h === 0
                        ? "12 AM"
                        : h < 12
                          ? `${h} AM`
                          : h === 12
                            ? "12 PM"
                            : `${h - 12} PM`;
                    return (
                      <div
                        key={h}
                        className="grid border-b border-[var(--border-subtle)]"
                        style={{ gridTemplateColumns: "64px repeat(7, 1fr)" }}
                      >
                        <div className="border-r border-[var(--border-subtle)] py-1.5 pr-2 text-right text-[10px] text-[var(--text-tertiary)]">
                          {label}
                        </div>
                        {weekDays.map((day) => {
                          const dk = toDateKey(day);
                          const hourItems = (itemsByDate[dk] || []).filter(
                            (it) => {
                              if (it.all_day) return false;
                              return new Date(it.date).getHours() === h;
                            },
                          );
                          return (
                            <div
                              key={dk}
                              className="border-r border-[var(--border-subtle)] last:border-r-0 min-h-[40px] px-0.5 py-0.5"
                              onClick={() => setSelectedDate(day)}
                            >
                              {hourItems.map((it) => {
                                const c = colorClasses(it.color);
                                return (
                                  <div
                                    key={it.id}
                                    className={`rounded-[var(--radius-sm)] px-1 py-0.5 text-[9px] font-medium truncate ${c.bg} ${c.text}`}
                                  >
                                    {it.title}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

        {/* ============ MONTH VIEW ============ */}
        {viewMode === "month" && (
          <>
            {/* Day-of-week header row */}
            <div className="grid grid-cols-7 mb-px">
              {dayNames.map((dn) => (
                <div
                  key={dn}
                  className="py-1.5 text-center text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]"
                >
                  {dn}
                </div>
              ))}
            </div>

            {/* Month grid */}
            <div className="grid grid-cols-7 border-t border-l border-[var(--border-default)]">
              {gridDays.map((day) => {
                const key = toDateKey(day);
                const isCurrentMonth =
                  day.getMonth() === currentMonth.getMonth();
                const isToday = isSameDay(day, today);
                const isSelected = selectedDate
                  ? isSameDay(day, selectedDate)
                  : false;
                const dayItems = itemsByDate[key] ?? [];
                const maxPills = 3;
                const overflow = dayItems.length - maxPills;

                return (
                  <div
                    key={key}
                    onClick={() => setSelectedDate(day)}
                    className={`relative min-h-[90px] border-r border-b border-[var(--border-default)] p-1 cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-[var(--color-interactive-primary)]/8"
                        : "hover:bg-[var(--surface-base)]"
                    }`}
                  >
                    {/* Day number */}
                    <div className="flex items-center justify-between mb-0.5">
                      <span
                        className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                          isToday
                            ? "bg-[var(--color-interactive-primary)] text-[var(--text-inverse)] font-bold"
                            : isCurrentMonth
                              ? "text-[var(--text-primary)]"
                              : "text-[var(--text-tertiary)]"
                        }`}
                      >
                        {day.getDate()}
                      </span>
                      {/* Quick-add icon */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDate(day);
                          setShowCreate(true);
                        }}
                        className="h-4 w-4 rounded text-[var(--text-tertiary)] opacity-0 group-hover:opacity-100 hover:text-[var(--color-interactive-primary)] transition-opacity"
                        style={{ opacity: isSelected ? 1 : undefined }}
                        aria-label={`Add item on ${key}`}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    {/* Event pills */}
                    <div className="space-y-px">
                      {dayItems.slice(0, maxPills).map((item) => {
                        const c = colorClasses(item.color);
                        return (
                          <div
                            key={item.id}
                            className={`truncate rounded-[var(--radius-sm)] px-1 py-px text-[10px] leading-tight ${c.bg} ${c.text}`}
                            title={`${item.source_label}: ${item.title}`}
                          >
                            <span className="font-semibold opacity-70">
                              {item.source_label}
                            </span>{" "}
                            {item.title}
                          </div>
                        );
                      })}
                      {overflow > 0 && (
                        <div className="px-1 text-[10px] font-medium text-[var(--text-tertiary)]">
                          +{overflow} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* ================================================================
          Day detail sidebar — slides in when a date is selected
          ================================================================ */}
      {selectedDate && (
        <aside className="w-80 shrink-0 border-l border-[var(--border-default)] bg-[var(--surface-base)] pl-4 animate-in slide-in-from-right-4 duration-200">
          {/* Sidebar header */}
          <div className="flex items-center justify-between mb-4 pt-1">
            <h4 className="text-sm font-semibold text-[var(--text-primary)]">
              {formatLongDate(selectedDate)}
            </h4>
            <button
              onClick={() => {
                setSelectedDate(null);
                setShowCreate(false);
              }}
              className="rounded-[var(--radius-md)] p-1 text-[var(--text-tertiary)] hover:bg-[var(--surface-base)] transition-colors"
              aria-label="Close sidebar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 mb-4">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setShowCreate(true);
                setCreateType("event");
              }}
            >
              <Plus className="h-3.5 w-3.5" /> Event
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setShowCreate(true);
                setCreateType("task");
              }}
            >
              <Plus className="h-3.5 w-3.5" /> Task
            </Button>
          </div>

          {/* ---- Inline create form ---- */}
          {showCreate && (
            <Card padding="sm" className="mb-4">
              <div className="space-y-2">
                <div className="flex gap-1 mb-2">
                  <button
                    onClick={() => setCreateType("event")}
                    className={`flex-1 rounded-[var(--radius-md)] py-1 text-xs font-medium transition-colors ${
                      createType === "event"
                        ? "bg-[var(--color-interactive-primary)] text-[var(--text-inverse)]"
                        : "bg-[var(--surface-sunken)] text-[var(--text-secondary)]"
                    }`}
                  >
                    Event
                  </button>
                  <button
                    onClick={() => setCreateType("task")}
                    className={`flex-1 rounded-[var(--radius-md)] py-1 text-xs font-medium transition-colors ${
                      createType === "task"
                        ? "bg-[var(--color-interactive-primary)] text-[var(--text-inverse)]"
                        : "bg-[var(--surface-sunken)] text-[var(--text-secondary)]"
                    }`}
                  >
                    Task
                  </button>
                </div>

                <Input
                  value={createTitle}
                  onChange={(e) => setCreateTitle(e.target.value)}
                  placeholder={
                    createType === "event" ? "Event title..." : "Task title..."
                  }
                  className="text-sm"
                />

                {createType === "event" ? (
                  <>
                    <label className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                      <input
                        type="checkbox"
                        checked={createAllDay}
                        onChange={(e) => setCreateAllDay(e.target.checked)}
                        className="rounded"
                      />
                      All day
                    </label>
                    {!createAllDay && (
                      <div className="flex items-center gap-2">
                        <input
                          type="time"
                          value={createStartTime}
                          onChange={(e) => setCreateStartTime(e.target.value)}
                          className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 py-1 text-xs text-[var(--text-primary)]"
                        />
                        <span className="text-[var(--text-tertiary)] text-xs">
                          to
                        </span>
                        <input
                          type="time"
                          value={createEndTime}
                          onChange={(e) => setCreateEndTime(e.target.value)}
                          className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 py-1 text-xs text-[var(--text-primary)]"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <select
                    value={createPriority}
                    onChange={(e) => setCreatePriority(e.target.value)}
                    className="w-full rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 py-1.5 text-xs text-[var(--text-primary)]"
                  >
                    <option value="low">Low priority</option>
                    <option value="medium">Medium priority</option>
                    <option value="high">High priority</option>
                    <option value="urgent">Urgent</option>
                  </select>
                )}

                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    onClick={handleCreateSubmit}
                    isLoading={
                      createTaskMutation.isPending ||
                      createEventMutation.isPending
                    }
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setShowCreate(false);
                      setCreateTitle("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* ---- Day items list ---- */}
          {selectedDayItems.length === 0 && !showCreate && (
            <p className="text-sm text-[var(--text-tertiary)] italic">
              No items on this day.
            </p>
          )}

          <div className="space-y-2 overflow-y-auto max-h-[calc(100vh-320px)]">
            {selectedDayItems.map((item) => {
              const c = colorClasses(item.color);
              const priorityVariant =
                item.priority === "urgent" || item.priority === "high"
                  ? "danger"
                  : item.priority === "medium"
                    ? "warning"
                    : "default";
              const statusVariant =
                item.status === "done"
                  ? "success"
                  : item.status === "in_progress"
                    ? "warning"
                    : "default";

              return (
                <Card key={item.id} padding="sm" className="hover-lift">
                  <div className="flex items-start gap-2">
                    {/* Color dot */}
                    <span
                      className={`mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full ${c.dot}`}
                    />
                    <div className="flex-1 min-w-0">
                      {/* Source badge + title */}
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span
                          className={`inline-flex items-center rounded-[var(--radius-sm)] px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide ${c.bg} ${c.text}`}
                        >
                          {item.source_label}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-[var(--text-primary)] leading-snug">
                        {item.title}
                      </p>

                      {/* Status / priority badges */}
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        {item.status && (
                          <Badge variant={statusVariant}>
                            {item.status.replace(/_/g, " ")}
                          </Badge>
                        )}
                        {item.priority && (
                          <Badge variant={priorityVariant}>
                            {item.priority}
                          </Badge>
                        )}
                        {!item.all_day && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-[var(--text-tertiary)]">
                            <Clock className="h-2.5 w-2.5" />
                            {new Date(item.date).toLocaleTimeString("en-US", {
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </span>
                        )}
                      </div>

                      {/* Description */}
                      {item.description && (
                        <p className="mt-1 text-xs text-[var(--text-secondary)] line-clamp-2">
                          {item.description}
                        </p>
                      )}

                      {/* Entity link */}
                      {item.link && (
                        <a
                          href={item.link}
                          className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-[var(--color-interactive-primary)] hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          View details
                        </a>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </aside>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notes Tab — preserved from existing implementation
// ---------------------------------------------------------------------------

function NotesTab() {
  const qc = useQueryClient();
  const { data: notes, isLoading } = useQuery<
    {
      id: string;
      title: string;
      content: string;
      visibility: string;
      updated_at: string;
    }[]
  >({
    queryKey: ["planner-notes"],
    queryFn: async () => (await apiClient.get("/api/v1/planner/notes")).data,
  });
  const createMutation = useMutation({
    mutationFn: async (title: string) =>
      (await apiClient.post("/api/v1/planner/notes", { title, content: "" }))
        .data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["planner-notes"] });
      toast.success("Note created");
    },
    onError: () => toast.error("Failed to create note"),
  });
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/planner/notes/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["planner-notes"] });
      toast.success("Note deleted");
    },
    onError: () => toast.error("Failed to delete note"),
  });
  const [newTitle, setNewTitle] = useState("");

  return (
    <div className="space-y-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (newTitle.trim()) {
            createMutation.mutate(newTitle);
            setNewTitle("");
          }
        }}
        className="flex gap-2"
      >
        <Input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="New note title..."
          className="flex-1"
        />
        <Button type="submit" size="sm">
          <Plus className="h-4 w-4" />
        </Button>
      </form>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 stagger-children">
          {notes?.map((n) => (
            <Card key={n.id} className="hover-lift">
              <div className="flex items-start justify-between">
                <h3 className="font-medium text-[var(--text-primary)]">
                  {n.title}
                </h3>
                <button
                  onClick={() => deleteMutation.mutate(n.id)}
                  className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="mt-2 text-xs text-[var(--text-secondary)] line-clamp-3">
                {n.content || "Empty note"}
              </p>
              <div className="mt-3 flex items-center gap-2">
                <Badge
                  variant={n.visibility === "private" ? "default" : "info"}
                >
                  {n.visibility}
                </Badge>
                <span className="text-[10px] text-[var(--text-tertiary)]">
                  {new Date(n.updated_at).toLocaleDateString()}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main PlannerPage — two-tab layout
// ---------------------------------------------------------------------------

function PlannerPage() {
  const [activeTab, setActiveTab] = useState<PlannerTab>("calendar");

  const tabs: { key: PlannerTab; label: string; icon: typeof Calendar }[] = [
    { key: "calendar", label: "Calendar", icon: Calendar },
    { key: "notes", label: "Notes", icon: StickyNote },
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[var(--text-primary)]">Planner</h2>

      {/* Tab navigation */}
      <div className="border-b border-[var(--border-default)]">
        <nav className="flex gap-6">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-2 border-b-2 pb-3 text-sm font-medium transition-colors ${
                activeTab === t.key
                  ? "border-[var(--color-interactive-primary)] text-[var(--color-interactive-primary)]"
                  : "border-transparent text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === "calendar" && <CalendarTab />}
      {activeTab === "notes" && <NotesTab />}
    </div>
  );
}

export default PlannerPage;
